import { esc } from "./html"
import { speedMphLabel } from "./web_location_policy"
import { CLIENT_CONTRACT } from "./client_contract"

// Friend presence helpers + live map markers, ported from the app
// (react-native/src/format.js + FriendMarkers.js) so both platforms present
// friends the same way: same colors, names, status lines, and marker glide.

export const FRIEND_COLORS = CLIENT_CONTRACT.mobile.presentation.friendColors
export const friendColor = id => FRIEND_COLORS[Number(id) % FRIEND_COLORS.length]

// Friendly display name: trimmed display_name, else the email local-part.
export const friendName = f => (f?.display_name && f.display_name.trim()) || (f?.email || "").split("@")[0]

// Compass heading (degrees from north) → cardinal label.
export function cardinal(deg) {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"]
  const normalized = ((deg % 360) + 360) % 360
  return dirs[Math.round(normalized / 45) % 8]
}

// Compact relative age for a timestamp: "just now", "3m ago", "2h ago", "1d ago".
export function timeAgoText(at) {
  const t = new Date(at).getTime()
  if (!Number.isFinite(t)) return ""
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (sec < 60) return "just now"
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`
  return `${Math.round(sec / 86400)}d ago`
}

// One-line status for a friend popup: tracking feed freshness beats generic
// online text, then live motion, then trip/online fallback.
export function friendStatusLine(f) {
  if (!f) return ""
  // Deliberate parked state (device sent a final resting beat before iOS paused
  // its stationary GPS) — honest and calm, unlike an unexplained signal loss.
  if (f.resting && f.resting_at) {
    return `Resting - since ${timeAgoText(f.resting_at)}`
  }
  if (f.signal_lost && f.signal_lost_at) {
    return `Signal lost ${timeAgoText(f.signal_lost_at)}`
  }
  if (f.location_source === "last_seen" && f.location_recorded_at) {
    return `Last seen ${timeAgoText(f.location_recorded_at)}`
  }
  if (f.location_source === "trip_point" && (f.location_recorded_at || f.track_last_point_at)) {
    return statusWithMotion(`Tracking - updated ${timeAgoText(f.location_recorded_at || f.track_last_point_at)}`, f)
  }
  if (f.location_source === "presence" && (f.location_recorded_at || f.presence_last_seen_at)) {
    return statusWithMotion(`Live - updated ${timeAgoText(f.location_recorded_at || f.presence_last_seen_at)}`, f)
  }
  const parts = motionParts(f)
  if (parts.length) return parts.join(" · ")
  return f.on_trip ? "On a trip" : f.online ? "Online" : "Offline"
}

function statusWithMotion(prefix, f) {
  const parts = motionParts(f)
  return [prefix, ...parts].join(" · ")
}

function motionParts(f) {
  const parts = []
  const speedLabel = speedMphLabel(f.speed)
  if (speedLabel !== "–") parts.push(f.speed < 0.5 ? "stopped" : `${speedLabel} mph`)
  if (f.heading != null && f.heading >= 0) parts.push(`heading ${cardinal(f.heading)}`)
  return parts
}

const GLIDE = CLIENT_CONTRACT.mobile.presentation.friendMarkerGlide

// DOM markers for friends with live positions. Location presence IS the gate:
// the server only sends lat/lon when the friend is genuinely locatable from an
// active trip, so anyone with coordinates gets a marker. Markers GLIDE between
// presence updates instead of jumping every poll.
export function createFriendMarkers(map, maplibregl) {
  const markers = new Map() // id -> { marker, popup, el, display, target }
  let raf = null

  const popupHtml = f =>
    `<span class="font-semibold">${esc(friendName(f))}</span><br>${esc(friendStatusLine(f))}`

  function upsert(f) {
    let entry = markers.get(f.id)
    if (!entry) {
      const el = document.createElement("div")
      el.innerHTML = `<div class="friend-marker">` +
        `<span class="friend-marker-dot" style="background:${friendColor(f.id)}"></span>` +
        `<span class="friend-marker-label"></span></div>`
      const popup = new maplibregl.Popup({ offset: 14, maxWidth: "260px" })
      const marker = new maplibregl.Marker({ element: el }).setLngLat([f.lon, f.lat]).setPopup(popup).addTo(map)
      // Newcomers appear in place — no glide-in from nowhere.
      entry = { marker, popup, el, display: { lat: f.lat, lon: f.lon }, target: { lat: f.lat, lon: f.lon } }
      markers.set(f.id, entry)
    }
    entry.target = { lat: f.lat, lon: f.lon }
    entry.el.querySelector(".friend-marker-label").textContent = friendName(f)
    // Dim a "last seen" dot so a signal-lost friend reads as stale, not live.
    entry.el.firstChild.style.opacity = f.location_source === "last_seen" ? "0.5" : "1"
    entry.popup.setHTML(popupHtml(f))
  }

  function step() {
    let moving = false
    markers.forEach(entry => {
      const { display, target } = entry
      const dlat = target.lat - display.lat, dlon = target.lon - display.lon
      if (Math.abs(dlat) > 1e-7 || Math.abs(dlon) > 1e-7) {
        display.lat += dlat * GLIDE
        display.lon += dlon * GLIDE
        moving = true
      } else {
        display.lat = target.lat
        display.lon = target.lon
      }
      entry.marker.setLngLat([display.lon, display.lat])
    })
    raf = moving ? requestAnimationFrame(step) : null
  }

  return {
    // `friends` is the presence payload; only entries with lat/lon get a marker.
    update(friends) {
      const located = (friends || []).filter(f => f.lat != null && f.lon != null)
      const ids = new Set(located.map(f => f.id))
      markers.forEach((entry, id) => {
        if (!ids.has(id)) { entry.marker.remove(); markers.delete(id) }
      })
      located.forEach(upsert)
      if (raf == null && markers.size) raf = requestAnimationFrame(step)
    },
    destroy() {
      if (raf != null) cancelAnimationFrame(raf)
      raf = null
      markers.forEach(entry => entry.marker.remove())
      markers.clear()
    }
  }
}
